import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, Camera, Inbox, ListChecks, MessageCircleQuestion, Send, Sparkles, Trophy, Upload } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTeam } from "@/lib/team";

const PHOTOS = [
  [Camera, "Snap each item", "Open Upload. Take 1–6 photos of one item, tap Next item, and repeat for the whole pile."],
  [Send, "Send to your lister", "Turn on Send to lister, add a note like \u201cList on Poshmark\u201d, then tap Upload. Done."],
  [ListChecks, "Mark drafts ready", "In Drafts, tap Ready on drafts you made so your lister sees them first."],
];
const LISTER = [
  [Inbox, "Open your Queue", "Everything sent to you waits here, oldest first. The big card is your next item."],
  [Sparkles, "Write it", "Tap AI write to get a listing in seconds, or Write myself. Fix anything that looks off."],
  [Upload, "Post to eBay", "Tap Post to eBay. The next item opens by itself, so just keep going."],
  [MessageCircleQuestion, "Stuck? Ask", "Tap Ask owner on any item. It's set aside until they answer, then comes back to the top."],
];
const BOTH_SNAP = [Camera, "Snap, then list", "Open Upload and photograph each item. Then open Queue to write and post them, one after another."];
const END = [[Trophy, "Race the week", "Every item you list moves your Pac-Man on the weekly board. Turn on alerts so you never miss new work."], [Bell, "", ""]];

const key = (email) => `snaplist.vaTour.${(email || "").toLowerCase()}`;
export const openVaTour = () => window.dispatchEvent(new Event("snaplist:va-tour"));

// First login for an assistant: a short animated walk-through of their job. Replay from the ? button.
export default function VaTour() {
  const { user } = useAuth();
  const { isAssistant, role } = useTeam();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  useEffect(() => {
    if (isAssistant && !localStorage.getItem(key(user?.email))) setOpen(true);
    const replay = () => { setI(0); setOpen(true); };
    window.addEventListener("snaplist:va-tour", replay);
    return () => window.removeEventListener("snaplist:va-tour", replay);
  }, [isAssistant, user?.email]);
  if (!open || !isAssistant) return null;
  const steps = role === "photos" ? [...PHOTOS, END[0]] : role === "lister" ? [...LISTER, END[0]] : [BOTH_SNAP, ...LISTER.slice(1), END[0]];
  const [Icon, title, body] = steps[i];
  const last = i === steps.length - 1;
  const done = () => { localStorage.setItem(key(user?.email), "1"); setOpen(false); window.dispatchEvent(new Event("snaplist:va-tour-done")); };
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-950/70 p-3 backdrop-blur-sm sm:items-center" data-testid="va-tour">
      <div className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="relative flex h-44 items-center justify-center overflow-hidden bg-[#0053DA]">
          <motion.div className="absolute h-40 w-40 rounded-full bg-white/10" animate={{ scale: [1, 1.35, 1] }} transition={{ duration: 2.2, repeat: Infinity }} />
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ y: 30, opacity: 0, rotate: -12 }} animate={{ y: 0, opacity: 1, rotate: 0 }} exit={{ y: -30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 18 }}
              className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-white text-[#0053DA] shadow-lg">
              <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}><Icon className="h-10 w-10" /></motion.span>
            </motion.div>
          </AnimatePresence>
          <span className="absolute left-4 top-3 font-mono text-[10px] uppercase tracking-widest text-white/70" data-testid="va-tour-step">Step {i + 1} of {steps.length}</span>
        </div>
        <div className="p-5">
          <AnimatePresence mode="wait">
            <motion.div key={i} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.25 }}>
              <h2 className="text-xl font-bold text-slate-900" data-testid="va-tour-title">{title}</h2>
              <p className="mt-1 text-sm text-slate-600">{body}</p>
            </motion.div>
          </AnimatePresence>
          <div className="mt-4 flex items-center justify-between">
            <div className="flex gap-1.5">{steps.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? "w-5 bg-[#0053DA]" : "w-1.5 bg-slate-200"}`} />)}</div>
            <div className="flex items-center gap-2">
              {!last && <button type="button" onClick={done} className="text-xs text-slate-400 hover:text-slate-600" data-testid="va-tour-skip">Skip</button>}
              <button type="button" onClick={() => (last ? done() : setI(i + 1))} data-testid="va-tour-next"
                className="rounded-full bg-[#0053DA] px-5 py-2 text-sm font-semibold text-white transition-transform active:scale-95">{last ? "Let's go" : "Next"}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
